/**
 * Advisor Network Page - Professional Ecosystem
 * ==============================================
 * SlabReality Advisor Intelligence Layer
 * 
 * Premium professional infrastructure for luxury stone consulting.
 * NOT a marketplace. NOT a social network.
 * 
 * @version 1.0 - AIL Foundation
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, MapPin, Filter, Search, ChevronDown,
  Globe, CheckCircle, X, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { AdvisorCardFull } from '../components/advisor/AdvisorIntelligenceCard';

const API = process.env.REACT_APP_BACKEND_URL || '';

// =====================================================
// FILTER PILL COMPONENT
// =====================================================
const FilterPill = ({ label, active, onClick, count }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
      active 
        ? 'bg-[#C8A36A] text-black' 
        : 'bg-white/[0.03] text-[#F5F1E8]/70 border border-white/10 hover:border-[#C8A36A]/30 hover:text-[#F5F1E8]'
    }`}
  >
    {label}
    {count !== undefined && (
      <span className={`ml-1.5 ${active ? 'text-black/60' : 'text-[#F5F1E8]/40'}`}>
        ({count})
      </span>
    )}
  </button>
);

// =====================================================
// MAIN PAGE COMPONENT
// =====================================================
const AdvisorNetworkPage = () => {
  const navigate = useNavigate();
  
  // State
  const [advisors, setAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    specialty: null,
    city: null,
    onlineOnly: false,
    availableOnly: false
  });
  const [specialties, setSpecialties] = useState([]);

  // Load advisors
  useEffect(() => {
    loadAdvisors();
    loadSpecialties();
  }, [filters]);

  const loadAdvisors = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.specialty) params.append('specialty', filters.specialty);
      if (filters.city) params.append('city', filters.city);
      if (filters.onlineOnly) params.append('online_only', 'true');
      if (filters.availableOnly) params.append('available_only', 'true');
      
      const response = await axios.get(`${API}/api/ail/advisors?${params}`);
      
      if (response.data?.advisors) {
        setAdvisors(response.data.advisors);
      }
    } catch (err) {
      console.error('[AIL] Failed to load advisors:', err);
      toast.error('Errore nel caricamento degli advisor');
    }
    setLoading(false);
  };

  const loadSpecialties = async () => {
    try {
      const response = await axios.get(`${API}/api/ail/specialty-types`);
      if (response.data?.specialties) {
        setSpecialties(response.data.specialties);
      }
    } catch (err) {
      console.error('[AIL] Failed to load specialties:', err);
    }
  };

  // Filter advisors by search term
  const filteredAdvisors = advisors.filter(advisor => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      advisor.full_name?.toLowerCase().includes(term) ||
      advisor.company?.toLowerCase().includes(term) ||
      advisor.city?.toLowerCase().includes(term) ||
      advisor.title?.toLowerCase().includes(term)
    );
  });

  // Get unique cities for filter
  const uniqueCities = [...new Set(advisors.map(a => a.city).filter(Boolean))];

  const handleAdvisorClick = (advisor) => {
    // Navigate to advisor detail or show modal
    console.log('[AIL] Advisor clicked:', advisor.slug || advisor.id);
    toast.info(`Profilo ${advisor.full_name} in arrivo...`);
  };

  const handleContactClick = (advisor) => {
    // Open contact modal
    toast.info(`Contatto ${advisor.full_name} - coming soon`);
  };

  const clearFilters = () => {
    setFilters({
      specialty: null,
      city: null,
      onlineOnly: false,
      availableOnly: false
    });
    setSearchTerm('');
  };

  const hasActiveFilters = filters.specialty || filters.city || filters.onlineOnly || filters.availableOnly || searchTerm;

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-b from-[#0A0A0A] to-[#050505] border-b border-white/5">
        {/* Subtle texture overlay using CSS gradient */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(200,163,106,0.03)_0%,transparent_70%)]" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C8A36A]/10 border border-[#C8A36A]/20 mb-6">
              <Users className="w-4 h-4 text-[#C8A36A]" />
              <span className="text-sm text-[#C8A36A] font-medium">Advisor Intelligence Layer</span>
            </div>
            
            {/* Title */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-[#F5F1E8] mb-6 tracking-tight">
              Professional<br />
              <span className="text-[#C8A36A]">Stone Network</span>
            </h1>
            
            {/* Description */}
            <p className="text-lg text-[#F5F1E8]/60 mb-8 max-w-xl">
              Connettiti con consulenti esperti specializzati in pietre naturali di lusso. 
              Supporto dalla selezione alla realizzazione.
            </p>

            {/* Stats */}
            <div className="flex items-center gap-8">
              <div>
                <div className="text-2xl font-serif text-[#C8A36A]">{advisors.length}</div>
                <div className="text-xs uppercase tracking-wider text-[#F5F1E8]/40">Advisor Attivi</div>
              </div>
              <div className="w-px h-10 bg-white/10" />
              <div>
                <div className="text-2xl font-serif text-[#C8A36A]">
                  {advisors.filter(a => a.online_status === 'online').length}
                </div>
                <div className="text-xs uppercase tracking-wider text-[#F5F1E8]/40">Online Ora</div>
              </div>
              <div className="w-px h-10 bg-white/10" />
              <div>
                <div className="text-2xl font-serif text-[#C8A36A]">
                  {uniqueCities.length}
                </div>
                <div className="text-xs uppercase tracking-wider text-[#F5F1E8]/40">Città</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="sticky top-0 z-30 bg-[#050505]/95 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F5F1E8]/40" />
              <input
                type="text"
                placeholder="Cerca advisor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white/[0.03] border border-white/10 rounded-xl text-[#F5F1E8] placeholder-[#F5F1E8]/30 focus:outline-none focus:border-[#C8A36A]/50 transition-colors"
              />
            </div>

            {/* Quick Filters */}
            <div className="flex flex-wrap items-center gap-2">
              <FilterPill 
                label="Online" 
                active={filters.onlineOnly}
                onClick={() => setFilters(f => ({ ...f, onlineOnly: !f.onlineOnly }))}
              />
              <FilterPill 
                label="Disponibile" 
                active={filters.availableOnly}
                onClick={() => setFilters(f => ({ ...f, availableOnly: !f.availableOnly }))}
              />
              
              {/* Specialty Dropdown */}
              <div className="relative">
                <select
                  value={filters.specialty || ''}
                  onChange={(e) => setFilters(f => ({ ...f, specialty: e.target.value || null }))}
                  className="appearance-none px-4 py-2 pr-8 bg-white/[0.03] border border-white/10 rounded-full text-sm text-[#F5F1E8]/70 focus:outline-none focus:border-[#C8A36A]/50 cursor-pointer"
                >
                  <option value="">Specialità</option>
                  {specialties.slice(0, 10).map(spec => (
                    <option key={spec.key} value={spec.key}>{spec.name_it}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F5F1E8]/40 pointer-events-none" />
              </div>

              {/* City Dropdown */}
              {uniqueCities.length > 0 && (
                <div className="relative">
                  <select
                    value={filters.city || ''}
                    onChange={(e) => setFilters(f => ({ ...f, city: e.target.value || null }))}
                    className="appearance-none px-4 py-2 pr-8 bg-white/[0.03] border border-white/10 rounded-full text-sm text-[#F5F1E8]/70 focus:outline-none focus:border-[#C8A36A]/50 cursor-pointer"
                  >
                    <option value="">Città</option>
                    {uniqueCities.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F5F1E8]/40 pointer-events-none" />
                </div>
              )}
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-[#F5F1E8]/60 hover:text-[#C8A36A] transition-colors"
              >
                <X className="w-4 h-4" />
                Cancella filtri
              </button>
            )}

            {/* Refresh */}
            <button
              onClick={loadAdvisors}
              disabled={loading}
              className="p-2.5 bg-white/[0.03] border border-white/10 rounded-xl text-[#F5F1E8]/60 hover:text-[#C8A36A] hover:border-[#C8A36A]/30 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Results count */}
        <div className="flex items-center justify-between mb-8">
          <p className="text-sm text-[#F5F1E8]/50">
            {loading ? 'Caricamento...' : `${filteredAdvisors.length} advisor trovati`}
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="bg-[#121212] rounded-xl border border-white/5 h-80 animate-pulse">
                <div className="h-20 bg-white/[0.03]" />
                <div className="p-4 space-y-4">
                  <div className="h-16 w-16 rounded-xl bg-white/[0.03]" />
                  <div className="h-4 w-3/4 bg-white/[0.03] rounded" />
                  <div className="h-3 w-1/2 bg-white/[0.03] rounded" />
                  <div className="flex gap-2">
                    <div className="h-6 w-20 bg-white/[0.03] rounded-full" />
                    <div className="h-6 w-20 bg-white/[0.03] rounded-full" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredAdvisors.length === 0 && (
          <div className="text-center py-20">
            <Users className="w-16 h-16 text-[#F5F1E8]/10 mx-auto mb-6" />
            <h3 className="text-xl font-medium text-[#F5F1E8] mb-2">Nessun advisor trovato</h3>
            <p className="text-[#F5F1E8]/50 mb-6">
              Prova a modificare i filtri di ricerca
            </p>
            <button
              onClick={clearFilters}
              className="px-6 py-3 bg-[#C8A36A] text-black rounded-xl font-medium hover:shadow-lg hover:shadow-[#C8A36A]/20 transition-all"
            >
              Cancella filtri
            </button>
          </div>
        )}

        {/* Advisor Grid */}
        {!loading && filteredAdvisors.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAdvisors.map((advisor, index) => (
              <AdvisorCardFull
                key={advisor.id || index}
                advisor={advisor}
                index={index}
                onClick={handleAdvisorClick}
                onContact={handleContactClick}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer CTA */}
      <div className="border-t border-white/5 bg-gradient-to-b from-[#050505] to-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="text-2xl md:text-3xl font-serif text-[#F5F1E8] mb-4">
            Non trovi il consulente giusto?
          </h2>
          <p className="text-[#F5F1E8]/50 mb-8 max-w-xl mx-auto">
            Compila una richiesta e il nostro team ti metterà in contatto con l'advisor più adatto alle tue esigenze.
          </p>
          <button 
            onClick={() => toast.info('Richiesta consulenza - coming soon')}
            className="px-8 py-4 bg-gradient-to-r from-[#C8A36A] to-[#D4AF37] rounded-xl text-black font-medium hover:shadow-lg hover:shadow-[#C8A36A]/20 transition-all"
          >
            Richiedi Consulenza Personalizzata
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdvisorNetworkPage;
